<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>

            <div class="page-header">
              <h2>IABF {{ adminFestivalYear }} — Meals &amp; Guests</h2>
              <div>
                <v-btn color="primary" class="mr-2" @click="openAddDialog(null)">+ Add Guest</v-btn>
                <v-btn outlined class="mr-2" @click="downloadOtherMealsCsv">↓ Meals CSV</v-btn>
                <v-btn outlined class="mr-2" @click="downloadBanquetCsv">↓ Banquet CSV</v-btn>
                <v-btn outlined class="mr-2" @click="downloadCoffeeShoppingList">↓ Coffee/Tea List</v-btn>
              </div>
            </div>

            <!-- Summary -->
            <div class="summary-bar mt-4 mb-4">
              <div class="summary-stat">
                <span class="stat-num">{{ allDancers.length + guestRegs.length }}</span>
                <span class="stat-label">Total People</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ paidGuests.length }}</span>
                <span class="stat-label">Guests</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ facultyAll.length }}</span>
                <span class="stat-label">Faculty + Faculty Guests</span>
              </div>
              <div class="summary-stat">
                <span class="stat-num">{{ paidGuests.filter(g => g.attendingShow).length }}</span>
                <span class="stat-label">Guests at Show</span>
              </div>
              <div class="summary-stat highlight-stat">
                <span class="stat-num">{{ welcomeDinnerCount }}</span>
                <span class="stat-label">Welcome Dinner</span>
              </div>
              <div class="summary-stat highlight-stat">
                <span class="stat-num">{{ finalBanquetCount }}</span>
                <span class="stat-label">Final Banquet</span>
              </div>
              <div v-if="isFinanceAdmin" class="summary-stat">
                <span class="stat-num">${{ paidGuests.reduce((s, g) => s + (g.totalCost || 0), 0) }}</span>
                <span class="stat-label">Guest Revenue</span>
              </div>
              <div v-if="isFinanceAdmin" class="summary-stat">
                <span class="stat-num">${{ paidGuests.filter(g => g.paymentStatus === 'unpaid').reduce((s, g) => s + (g.totalCost || 0), 0) }}</span>
                <span class="stat-label">Unpaid</span>
              </div>
            </div>

            <!-- Per-meal headcount -->
            <div class="meal-headcount mb-4">
              <div class="meal-headcount-title">Per-Meal Headcount <span class="meal-headcount-note">(dancers + all guests &amp; faculty attending each meal)</span></div>
              <div class="meal-headcount-grid">
                <div v-for="m in mealHeadcounts" :key="m.label" class="meal-count-cell">
                  <span class="meal-count-num">{{ m.count }}</span>
                  <span class="meal-count-label">{{ m.label }}</span>
                </div>
              </div>
            </div>

            <vue-good-table
              :columns="columns"
              :rows="tableRows"
              :search-options="{ enabled: true, placeholder: 'Search name, dancer, meals…' }"
              :row-style-class="rowStyleClass"
            >
              <div slot="emptystate">{{ loading ? 'Loading…' : 'No data.' }}</div>
              <template slot="table-row" slot-scope="props">

                <span v-if="props.column.field === 'statusDisplay'">
                  <span v-if="props.row.rowType === 'dancer'" class="type-tag dancer-tag">Dancer</span>
                  <span v-else-if="props.row.rowType === 'faculty'" class="type-tag faculty-tag">Faculty</span>
                  <span v-else-if="props.row.rowType === 'faculty_guest'" class="type-tag faculty-guest-tag">Faculty Guest</span>
                  <v-chip v-else small :color="statusColor(props.row.paymentStatus)" text-color="white">
                    {{ statusLabel(props.row.paymentStatus) }}
                  </v-chip>
                </span>

                <span v-else-if="props.column.field === 'actions'">
                  <v-btn v-if="props.row.rowType === 'dancer'" x-small outlined class="mr-1" @click="openAddDialog(props.row)">+ Guest</v-btn>
                  <v-btn v-if="props.row.rowType === 'faculty'" x-small outlined class="mr-1" @click="openAddDialog(props.row)">+ Guest</v-btn>
                  <v-btn v-if="props.row.rowType === 'faculty' && props.row.facultySource === 'record'" x-small outlined class="mr-1" @click="openFacultyStayDialog(props.row)">Arrive / leave</v-btn>
                  <v-btn v-if="props.row.rowType === 'faculty' && props.row.facultySource !== 'record'" x-small outlined class="mr-1" @click="openEditDialog(props.row)">Edit</v-btn>
                  <v-btn v-if="props.row.rowType === 'guest' || props.row.rowType === 'faculty_guest'" x-small outlined class="mr-1" @click="openEditDialog(props.row)">Edit</v-btn>
                  <v-btn
                    v-if="props.row.rowType === 'guest' && props.row.paymentStatus !== 'paid'"
                    x-small
                    :loading="!!markingPaidIds[props.row.id]"
                    @click="markPaid(props.row)"
                  >Mark Paid</v-btn>
                  <v-btn
                    v-if="props.row.rowType === 'faculty_guest' || (props.row.rowType === 'faculty' && props.row.facultySource !== 'record')"
                    x-small
                    color="error"
                    text
                    :loading="!!deletingIds[props.row.id]"
                    @click="deleteFaculty(props.row)"
                  >Delete</v-btn>
                </span>

                <span v-else>{{ props.formattedRow[props.column.field] }}</span>
              </template>
            </vue-good-table>

            <!-- Add Guest / Faculty Dialog -->
            <v-dialog v-model="showAddDialog" max-width="640" scrollable>
              <v-card>
                <v-card-title>{{ dialogTitle }}</v-card-title>
                <v-card-text style="max-height: 70vh; overflow-y: auto;">

                  <v-select
                    v-if="form.type === 'guest'"
                    v-model="form.dancerId"
                    :items="dancerSelectItems"
                    label="Dancer *"
                    :disabled="!!preselectedDancerId"
                  />
                  <v-select
                    v-else-if="form.type === 'faculty_guest'"
                    v-model="form.dancerId"
                    :items="facultySelectItems"
                    label="Faculty *"
                    :disabled="!!preselectedDancerId"
                  />
                  <v-text-field v-model="form.guestName" :label="guestNameLabel" />

                  <template v-if="form.type === 'guest'">
                    <div class="form-section-label">Showcase</div>
                    <v-radio-group v-model="form.attendingShow" row>
                      <v-radio label="Attending showcase" :value="true" />
                      <v-radio label="Not attending" :value="false" />
                    </v-radio-group>

                    <template v-if="form.attendingShow">
                      <div class="form-section-label">Awards Dinner &amp; Afterparty</div>
                      <v-radio-group v-model="form.awardsAfterparty" row>
                        <v-radio label="Yes" :value="true" />
                        <v-radio label="No" :value="false" />
                      </v-radio-group>
                      <v-select
                        v-if="form.awardsAfterparty"
                        v-model="form.afterpartyOption"
                        :items="afterpartySelectItems"
                        label="Afterparty option"
                      />
                    </template>

                    <div class="form-section-label">Lodging</div>
                    <v-radio-group v-model="form.lodging">
                      <v-radio label="Staying with dancer at Artist's Retreat" value="retreat" />
                      <v-radio label="Staying offsite" value="offsite" />
                    </v-radio-group>
                  </template>

                  <div class="form-section-label">Meals</div>
                  <v-checkbox v-model="form.allMeals" :label="isFreeType ? 'All Meals' : 'All Meals ($500)'" @change="onAllMealsChange" hide-details class="mb-2" />
                  <div v-if="!form.allMeals" class="meal-check-grid">
                    <v-checkbox
                      v-for="meal in MEAL_OPTIONS"
                      :key="meal.key"
                      v-model="form.meals"
                      :label="isFreeType ? meal.label : (meal.label + ' ($' + meal.price + ')')"
                      :value="meal.key"
                      dense
                      hide-details
                      @change="onBanquetCheck(meal.key)"
                    />
                  </div>

                  <div class="form-section-label mt-3">Dietary (same fields as dancers)</div>
                  <v-select
                    v-model="form.diet"
                    :items="dietSelectItems"
                    item-text="text"
                    item-value="value"
                    label="Diet"
                  />
                  <v-select
                    v-model="form.gluten"
                    :items="glutenSelectItems"
                    item-text="text"
                    item-value="value"
                    label="Gluten"
                    clearable
                  />
                  <v-select
                    v-model="form.foodAllergies"
                    :items="foodAllergiesOptions"
                    label="Food allergies"
                    multiple
                    chips
                    deletable-chips
                  />
                  <v-textarea v-model="form.dietaryNotes" label="Dietary notes" rows="2" auto-grow />
                  <v-textarea v-model="form.notes" label="Guest notes / accommodations" rows="2" auto-grow />

                  <template v-if="!isFreeType">
                    <div class="form-section-label">Payment</div>
                    <v-select v-model="form.paymentStatus" :items="['unpaid', 'paid', 'added_to_next']" label="Payment status" />
                    <v-text-field
                      v-model.number="form.priceOverride"
                      label="Price override (leave blank to use calculated total)"
                      type="number"
                      min="0"
                      prefix="$"
                      clearable
                    />

                    <div class="dialog-total">Total: ${{ dialogTotal }}</div>
                  </template>
                  <div v-if="saveError" class="error--text mt-1">{{ saveError }}</div>

                </v-card-text>
                <v-card-actions>
                  <v-btn
                    v-if="editingGuestId"
                    color="error"
                    text
                    :loading="deletingCurrent"
                    @click="deleteCurrent"
                  >Delete</v-btn>
                  <v-spacer />
                  <v-btn text @click="closeAddDialog">Cancel</v-btn>
                  <v-btn color="primary" :disabled="!canSave" :loading="saving" @click="saveGuest">{{ saveButtonLabel }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <v-dialog v-model="showFacultyStayDialog" max-width="480">
              <v-card v-if="facultyStayForm">
                <v-card-title>Arrive / leave — {{ facultyStayForm.name }}</v-card-title>
                <v-card-text>
                  <p class="caption mb-3">
                    Meals follow these dates: dinner on arrival day, breakfast through the morning they leave.
                    Defaults are the festival week ({{ defaultArrivalLabel }} – {{ defaultDepartureLabel }}).
                  </p>
                  <v-text-field
                    v-model="facultyStayForm.arrivalDate"
                    label="Arrives"
                    type="date"
                    hide-details
                    class="mb-2"
                  />
                  <v-text-field
                    v-model="facultyStayForm.departureDate"
                    label="Leaves"
                    type="date"
                    hide-details
                    class="mb-2"
                  />
                  <p class="caption">Meals: {{ facultyStayPreview }}</p>
                  <div v-if="facultyStayError" class="error--text mt-1">{{ facultyStayError }}</div>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn text @click="closeFacultyStayDialog">Cancel</v-btn>
                  <v-btn color="primary" :loading="savingFacultyStay" @click="saveFacultyStay">Save</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import 'vue-good-table/dist/vue-good-table.css'
import {
  DIETARY_OPTIONS,
  FOOD_ALLERGIES_OPTIONS,
  GLUTEN_OPTIONS,
  allergiesDisplayLine,
  buildFoodsNotConsumed,
  dietaryDisplayLine,
  glutenDisplayLine
} from '@/config/iabf-dietary'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { VueGoodTable } from 'vue-good-table'
import { db } from '@/firebase'
import { festivalWeek } from '@/config/iabf-festival-dates'
import {
  facultyMealsFromStay,
  facultyMealsLabel,
  facultyStayDates,
  formatFacultyStayDate
} from '@/utils/iabf-faculty'


const MEAL_OPTIONS = [
  { key: 'welcome_dinner', label: 'Welcome Dinner + Cocktail Party', price: 100 },
  { key: 'tue_breakfast', label: 'Tue Breakfast', price: 20 },
  { key: 'tue_lunch', label: 'Tue Lunch', price: 30 },
  { key: 'tue_dinner', label: 'Tue Dinner', price: 40 },
  { key: 'wed_breakfast', label: 'Wed Breakfast', price: 20 },
  { key: 'wed_lunch', label: 'Wed Lunch', price: 30 },
  { key: 'wed_dinner', label: 'Wed Dinner', price: 40 },
  { key: 'thu_breakfast', label: 'Thu Breakfast', price: 20 },
  { key: 'thu_lunch', label: 'Thu Lunch', price: 30 },
  { key: 'thu_dinner', label: 'Thu Dinner', price: 40 },
  { key: 'fri_breakfast', label: 'Fri Breakfast', price: 20 },
  { key: 'fri_lunch', label: 'Fri Lunch', price: 30 },
  // Transportation included with banquet in 2027+ — no separate transport option
  { key: 'fri_banquet', label: 'Fri Awards Banquet — Dinner', price: 100 },
  { key: 'sat_breakfast', label: 'Sat Breakfast', price: 20 }
]

const AFTERPARTY_OPTIONS = [
  { key: 'dinner_only', label: 'Afterparty & Dinner ($100)', price: 100 },
  { key: 'kids_menu', label: 'Kids Menu ($25)', price: 25 }
]

// Per-meal headcount groups. Legacy fri_banquet_transport still counted as Final Banquet.
const HEADCOUNT_MEALS = [
  { label: 'Welcome Dinner', keys: ['welcome_dinner'] },
  { label: 'Tue Breakfast', keys: ['tue_breakfast'] },
  { label: 'Tue Lunch', keys: ['tue_lunch'] },
  { label: 'Tue Dinner', keys: ['tue_dinner'] },
  { label: 'Wed Breakfast', keys: ['wed_breakfast'] },
  { label: 'Wed Lunch', keys: ['wed_lunch'] },
  { label: 'Wed Dinner', keys: ['wed_dinner'] },
  { label: 'Thu Breakfast', keys: ['thu_breakfast'] },
  { label: 'Thu Lunch', keys: ['thu_lunch'] },
  { label: 'Thu Dinner', keys: ['thu_dinner'] },
  { label: 'Fri Breakfast', keys: ['fri_breakfast'] },
  { label: 'Fri Lunch', keys: ['fri_lunch'] },
  { label: 'Final Banquet', keys: ['fri_banquet', 'fri_banquet_transport'] },
  { label: 'Sat Breakfast', keys: ['sat_breakfast'] }
]

const AFTERPARTY_TO_MEAL = {
  dinner_only: 'fri_banquet'
}

// The Awards Banquet is at a separate venue, so it exports on its own.
// Every other meal goes in the "all other meals" export.
// Include legacy transport key for 2026 records still in the DB.
const BANQUET_KEYS = ['fri_banquet', 'fri_banquet_transport']
const OTHER_MEAL_KEYS = MEAL_OPTIONS.map(m => m.key).filter(k => !BANQUET_KEYS.includes(k))

const MEAL_ABBREV = {
  welcome_dinner: 'Welcome Dinner',
  tue_breakfast: 'Tue B', tue_lunch: 'Tue L', tue_dinner: 'Tue D',
  wed_breakfast: 'Wed B', wed_lunch: 'Wed L', wed_dinner: 'Wed D',
  thu_breakfast: 'Thu B', thu_lunch: 'Thu L', thu_dinner: 'Thu D',
  fri_breakfast: 'Fri B', fri_lunch: 'Fri L',
  fri_banquet: 'Fri Banquet', fri_banquet_transport: 'Fri Banquet',
  sat_breakfast: 'Sat B'
}

function emptyForm () {
  return {
    type: 'guest',
    dancerId: null,
    guestName: '',
    attendingShow: null,
    awardsAfterparty: null,
    afterpartyOption: '',
    lodging: '',
    allMeals: false,
    meals: [],
    diet: '',
    gluten: '',
    foodAllergies: [],
    dietaryNotes: '',
    notes: '',
    paymentStatus: 'unpaid',
    priceOverride: null
  }
}

function csvEscape (val) {
  const s = String(val == null ? '' : val)
  return (s.includes(',') || s.includes('"') || s.includes('\n'))
    ? '"' + s.replace(/"/g, '""') + '"'
    : s
}

function abbrevName (name) {
  const parts = (name || '').trim().split(/\s+/)
  const fn = parts[0] || ''
  const li = parts.length > 1 ? parts[parts.length - 1].charAt(0) + '.' : ''
  return [fn, li].filter(Boolean).join(' ')
}

function parseName (full) {
  const parts = (full || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: '', lastName: parts[0] }
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] }
}

function capitalize (s) {
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

export default {
  mixins: [iabfAdminMixin],
  components: { VueGoodTable },
  data () {
    return {
      MEAL_OPTIONS,
      dietSelectItems: DIETARY_OPTIONS.map(o => ({ text: o.short, value: o.key })),
      glutenSelectItems: GLUTEN_OPTIONS.map(o => ({ text: o.label, value: o.key })),
      foodAllergiesOptions: FOOD_ALLERGIES_OPTIONS,
      loading: true,
      allDancers: [],
      facultyRecords: [],
      guestRegs: [],
      markingPaidIds: {},
      columns: [
        { label: 'Name', field: 'name', sortable: true },
        { label: 'Dancer', field: 'dancerNameDisplay', sortable: true },
        { label: 'Arrive', field: 'arriveDisplay' },
        { label: 'Leave', field: 'leaveDisplay' },
        { label: 'Meals', field: 'mealsLabel' },
        { label: 'Dietary', field: 'dietaryDisplay' },
        { label: 'Allergies', field: 'allergiesDisplay' },
        { label: 'Notes', field: 'notesDisplay' },
        { label: 'Status', field: 'statusDisplay', sortable: false },
        { label: '', field: 'actions', sortable: false, width: '160px' }
      ],
      showAddDialog: false,
      preselectedDancerId: null,
      editingGuestId: null,
      form: emptyForm(),
      saving: false,
      saveError: '',
      showFacultyStayDialog: false,
      facultyStayForm: null,
      savingFacultyStay: false,
      facultyStayError: '',
      deletingIds: {},
      deletingCurrent: false
    }
  },
  async created () {
    await this.reloadPage()
  },
  computed: {
    isFinanceAdmin () {
      return this.$store.getters.isFinanceAdmin
    },
    facultyAll () {
      const guests = this.guestRegs.filter(g => g.type === 'faculty_guest')
      if (this.facultyRecords.length) {
        return [...this.facultyRecords, ...guests]
      }
      return this.guestRegs.filter(g => g.type === 'faculty' || g.type === 'faculty_guest')
    },
    defaultArrivalLabel () {
      return formatFacultyStayDate(festivalWeek(this.adminFestivalYear).arrivalIso)
    },
    defaultDepartureLabel () {
      return formatFacultyStayDate(festivalWeek(this.adminFestivalYear).departureIso)
    },
    facultyStayPreview () {
      if (!this.facultyStayForm) {
        return ''
      }
      return facultyMealsLabel({
        arrivalDate: this.facultyStayForm.arrivalDate,
        departureDate: this.facultyStayForm.departureDate
      }, this.adminFestivalYear)
    },
    paidGuests () {
      return this.guestRegs.filter(g => !g.type || g.type === 'guest')
    },
    mealHeadcounts () {
      return HEADCOUNT_MEALS.map(m => ({ label: m.label, count: this.countAttendingMeal(m.keys) }))
    },
    welcomeDinnerCount () {
      return this.countAttendingMeal(['welcome_dinner'])
    },
    finalBanquetCount () {
      return this.countAttendingMeal(['fri_banquet', 'fri_banquet_transport'])
    },
    tableRows () {
      const rows = []
      const guestsByDancer = {}
      const guestsByFaculty = {}
      const facultyList = this.facultyPeopleForTable()

      for (const g of this.guestRegs) {
        if (g.type === 'faculty') {
          continue
        } else if (g.type === 'faculty_guest') {
          const key = g.dancerId || '__unknown__'
          if (!guestsByFaculty[key]) guestsByFaculty[key] = []
          guestsByFaculty[key].push(g)
        } else {
          const key = g.dancerId || '__unknown__'
          if (!guestsByDancer[key]) guestsByDancer[key] = []
          guestsByDancer[key].push(g)
        }
      }

      // Combined dancers + faculty, sorted alphabetically by last name
      const people = [
        ...this.allDancers.map(d => ({
          kind: 'dancer',
          id: d.id,
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          data: d
        })),
        ...facultyList.map(f => ({
          kind: 'faculty',
          id: f.id,
          firstName: f.firstName || '',
          lastName: f.lastName || '',
          data: f
        }))
      ]
      people.sort((a, b) => {
        const ln = (a.lastName || '').toLowerCase().localeCompare((b.lastName || '').toLowerCase())
        if (ln !== 0) return ln
        return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase())
      })

      for (const p of people) {
        if (p.kind === 'dancer') {
          const dancer = p.data
          const fullName = [dancer.firstName, dancer.lastName].filter(Boolean).join(' ')
          rows.push({
            id: dancer.id,
            rowType: 'dancer',
            name: fullName,
            dancerNameDisplay: '',
            arriveDisplay: '',
            leaveDisplay: '',
            mealsLabel: 'All Meals',
            dietaryDisplay: dancer.diet || '',
            allergiesDisplay: '',
            notesDisplay: '',
            statusDisplay: '',
            paymentStatus: null
          })
          const guests = guestsByDancer[dancer.id] || []
          guests.forEach(g => rows.push(this.buildGuestRow(g, fullName)))
        } else {
          const f = p.data
          const facultyName = f.guestName || [f.firstName, f.lastName].filter(Boolean).join(' ')
          rows.push(this.buildFacultyRow(f))
          const fGuests = guestsByFaculty[f.id] || []
          fGuests.forEach(g => rows.push(this.buildGuestRow(g, facultyName, 'faculty_guest')))
        }
      }

      // Orphaned guests (parent no longer in collection)
      const knownDancerIds = new Set(this.allDancers.map(d => d.id))
      const knownFacultyIds = new Set(facultyList.map(f => f.id))
      for (const g of this.guestRegs) {
        if (g.type === 'faculty') continue
        if (g.type === 'faculty_guest') {
          if (!knownFacultyIds.has(g.dancerId)) {
            rows.push(this.buildGuestRow(g, g.dancerName || '(unknown faculty)', 'faculty_guest'))
          }
        } else {
          if (!knownDancerIds.has(g.dancerId)) {
            rows.push(this.buildGuestRow(g, g.dancerName || '(unknown dancer)'))
          }
        }
      }

      return rows
    },
    dancerSelectItems () {
      return this.allDancers.map(d => ({
        text: [d.firstName, d.lastName].filter(Boolean).join(' '),
        value: d.id
      }))
    },
    facultySelectItems () {
      return this.facultyPeopleForTable()
        .map(f => ({
          text: f.guestName || [f.firstName, f.lastName].filter(Boolean).join(' ') || '(unnamed faculty)',
          value: f.id
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    afterpartySelectItems () {
      return AFTERPARTY_OPTIONS.map(o => ({ text: o.label, value: o.key }))
    },
    isFreeType () {
      return this.form.type === 'faculty' || this.form.type === 'faculty_guest'
    },
    dialogTitle () {
      if (this.editingGuestId) {
        if (this.form.type === 'faculty') return 'Edit Faculty'
        if (this.form.type === 'faculty_guest') return 'Edit Faculty Guest'
        return 'Edit Guest'
      }
      if (this.form.type === 'faculty_guest') return 'Add Faculty Guest'
      return 'Add Guest Registration'
    },
    guestNameLabel () {
      if (this.form.type === 'faculty') return 'Faculty Name *'
      if (this.form.type === 'faculty_guest') return 'Guest Name *'
      return 'Guest Name *'
    },
    saveButtonLabel () {
      if (this.form.type === 'faculty') return 'Save Faculty'
      if (this.form.type === 'faculty_guest') return 'Save Guest'
      return 'Save Guest'
    },
    dialogTotal () {
      if (this.isFreeType) return 0
      if (this.form.priceOverride !== null && this.form.priceOverride !== '') {
        return Number(this.form.priceOverride) || 0
      }
      let total = 0
      if (this.form.awardsAfterparty && this.form.afterpartyOption === 'kids_menu') total += 25
      if (this.form.allMeals) {
        total += 500
      } else {
        this.form.meals.forEach(key => {
          const m = MEAL_OPTIONS.find(o => o.key === key)
          if (m) total += m.price
        })
      }
      return total
    },
    canSave () {
      if (this.form.type === 'faculty') {
        return !!this.form.guestName.trim()
      }
      return !!this.form.dancerId && !!this.form.guestName.trim()
    }
  },
  watch: {
    adminFestivalYear () {
      this.reloadPage()
    },
    'form.afterpartyOption' () { this.syncBanquetFromAfterparty() },
    'form.awardsAfterparty' () { this.syncBanquetFromAfterparty() }
  },
  methods: {
    async reloadPage () {
      this.loading = true
      try {
        const [dancerSnap, guestSnap, faculty] = await Promise.all([
          db.collection(this.IABF.dancers).orderBy('lastName').get(),
          db.collection(this.IABF.guestRegistrations).get(),
          this.$store.dispatch('loadIabfFaculty').catch(() => [])
        ])
        this.allDancers = dancerSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.guestRegs = guestSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        this.facultyRecords = faculty || []
      } catch (e) {
        console.error('guests page load failed', e)
        this.allDancers = []
        this.guestRegs = []
        this.facultyRecords = []
      }
      this.loading = false
    },
    facultyPeopleForTable () {
      if (this.facultyRecords.length) {
        return this.facultyRecords.map(f => ({
          ...f,
          guestName: [f.firstName, f.lastName].filter(Boolean).join(' '),
          facultySource: 'record'
        }))
      }
      return this.guestRegs.filter(g => g.type === 'faculty').map(f => {
        const parsed = parseName(f.guestName)
        return {
          ...f,
          firstName: f.firstName || parsed.firstName,
          lastName: f.lastName || parsed.lastName,
          facultySource: 'legacy'
        }
      })
    },
    countAttendingMeal (keys) {
      // Dancers receive all meals, so every dancer attends every meal.
      let count = this.allDancers.length
      const faculty = this.facultyPeopleForTable()
      for (const f of faculty) {
        if (f.facultySource === 'record') {
          const meals = facultyMealsFromStay(f, this.adminFestivalYear)
          if (keys.some(k => meals.includes(k))) {
            count++
          }
        } else if (f.allMeals || (f.meals && keys.some(k => f.meals.includes(k)))) {
          count++
        }
      }
      for (const g of this.guestRegs) {
        if (g.type === 'faculty') {
          continue
        }
        if (g.allMeals || (g.meals && keys.some(k => g.meals.includes(k)))) {
          count++
        }
      }
      return count
    },
    guestEntry (g, typeLabel, attachedTo) {
      // Drop "standard" — caterers only care about anomalies.
      const dietKey = g.diet || g.dietaryRestriction || ''
      const dietLabel = dietKey && dietKey.toLowerCase() !== 'standard'
        ? dietaryDisplayLine(g) || capitalize(dietKey)
        : ''
      const allergyBits = []
      const allergyLine = allergiesDisplayLine(g)
      if (allergyLine && allergyLine !== 'None') {
        allergyBits.push(allergyLine)
      }
      const glutenLine = glutenDisplayLine(g)
      if (glutenLine && g.gluten && g.gluten !== 'ok') {
        allergyBits.push('Gluten: ' + glutenLine)
      }
      if (g.dietaryNotes) {
        allergyBits.push(g.dietaryNotes)
      }
      return {
        name: abbrevName(g.guestName),
        typeLabel,
        attachedTo,
        diet: dietLabel,
        allergies: allergyBits.join(' · ') || '',
        notes: g.notes || '',
        coffee: '',
        isDancer: false,
        allMeals: !!g.allMeals,
        meals: g.meals || []
      }
    },
    // Ordered people list (dancers + their guests, faculty + their guests, then
    // orphans), alphabetical by last name — shared by both meal exports.
    buildPeopleEntries () {
      const guestsByDancer = {}
      const guestsByFaculty = {}
      const facultyList = this.facultyPeopleForTable()
      for (const g of this.guestRegs) {
        if (g.type === 'faculty') {
          continue
        } else if (g.type === 'faculty_guest') {
          const key = g.dancerId || '__unknown__'
          if (!guestsByFaculty[key]) guestsByFaculty[key] = []
          guestsByFaculty[key].push(g)
        } else {
          const key = g.dancerId || '__unknown__'
          if (!guestsByDancer[key]) guestsByDancer[key] = []
          guestsByDancer[key].push(g)
        }
      }

      const people = [
        ...this.allDancers.map(d => ({
          kind: 'dancer',
          lastName: d.lastName || '',
          firstName: d.firstName || '',
          data: d
        })),
        ...facultyList.map(f => ({
          kind: 'faculty',
          lastName: f.lastName || '',
          firstName: f.firstName || '',
          data: f
        }))
      ]
      people.sort((a, b) => {
        const ln = (a.lastName || '').toLowerCase().localeCompare((b.lastName || '').toLowerCase())
        if (ln !== 0) return ln
        return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase())
      })

      const entries = []
      for (const p of people) {
        if (p.kind === 'dancer') {
          const dancer = p.data
          const dancerFullName = [dancer.firstName, dancer.lastName].filter(Boolean).join(' ')
          // Dancer dietary fields live under different keys than guest ones
          // (Profile.vue: diet / foodsNotConsumed / foodAllergies / coffee / dietaryNotes).
          // For caterers: drop "standard", list restrictions comma-separated,
          // skip coffee/tea (handled by its own shopping-list export).
          const restrictions = Array.isArray(dancer.foodsNotConsumed) ? dancer.foodsNotConsumed.filter(Boolean) : []
          const dietPieces = []
          if (dancer.diet && dancer.diet.toLowerCase() !== 'standard') {
            dietPieces.push(capitalize(dancer.diet))
          }
          dietPieces.push(...restrictions)
          const allergies = Array.isArray(dancer.foodAllergies)
            ? dancer.foodAllergies.filter(a => a && a !== 'No Allergies').join(', ')
            : ''
          entries.push({
            name: abbrevName(dancerFullName),
            typeLabel: 'Dancer',
            attachedTo: '',
            diet: dietPieces.join(', '),
            allergies,
            notes: dancer.dietaryNotes || '',
            coffee: dancer.coffee || '',
            room: dancer.room || '',
            isDancer: true,
            allMeals: true,
            meals: []
          })
          for (const g of (guestsByDancer[dancer.id] || [])) {
            entries.push(this.guestEntry(g, 'Guest', dancerFullName))
          }
        } else {
          const f = p.data
          const facultyFullName = f.guestName || [f.firstName, f.lastName].filter(Boolean).join(' ')
          entries.push(this.facultyMealEntry(f, facultyFullName))
          for (const g of (guestsByFaculty[f.id] || [])) {
            entries.push(this.guestEntry(g, 'Faculty Guest', facultyFullName))
          }
        }
      }

      // Orphaned guests (parent no longer in collection)
      const knownDancerIds = new Set(this.allDancers.map(d => d.id))
      const knownFacultyIds = new Set(facultyList.map(f => f.id))
      for (const g of this.guestRegs) {
        if (g.type === 'faculty') continue
        if (g.type === 'faculty_guest') {
          if (!knownFacultyIds.has(g.dancerId)) {
            entries.push(this.guestEntry(g, 'Faculty Guest', g.dancerName || ''))
          }
        } else if (!knownDancerIds.has(g.dancerId)) {
          entries.push(this.guestEntry(g, 'Guest', g.dancerName || ''))
        }
      }

      return entries
    },
    // Dancers get all meals; everyone else attends if allMeals or a listed meal matches.
    entryAttends (entry, keys) {
      if (entry.isDancer || entry.allMeals) return true
      return keys.some(k => entry.meals.includes(k))
    },
    // At the "all other meals" venue, attending the Welcome Dinner and nothing else.
    isWelcomeDinnerOnly (entry) {
      if (entry.isDancer || entry.allMeals) return false
      if (!entry.meals.includes('welcome_dinner')) return false
      return !OTHER_MEAL_KEYS.some(k => k !== 'welcome_dinner' && entry.meals.includes(k))
    },
    triggerCsvDownload (filename, headers, rows) {
      const csv = [headers, ...rows].map(r => r.map(csvEscape).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    },
    downloadBanquetCsv () {
      const headers = ['Name', 'Type', 'Attached To', 'Diet', 'Allergies', 'Notes']
      const rows = this.buildPeopleEntries()
        .filter(e => this.entryAttends(e, BANQUET_KEYS))
        .map(e => [e.name, e.typeLabel, e.attachedTo, e.diet, e.allergies, e.notes])
      this.triggerCsvDownload(`iabf-${this.adminFestivalYear}-awards-banquet.csv`, headers, rows)
    },
    downloadOtherMealsCsv () {
      const headers = ['Name', 'Type', 'Attached To', 'Diet', 'Allergies', 'Notes', 'Welcome Dinner Only']
      const rows = this.buildPeopleEntries()
        .filter(e => this.entryAttends(e, OTHER_MEAL_KEYS))
        .map(e => [e.name, e.typeLabel, e.attachedTo, e.diet, e.allergies, e.notes, this.isWelcomeDinnerOnly(e) ? 'Y' : ''])
      this.triggerCsvDownload(`iabf-${this.adminFestivalYear}-meals.csv`, headers, rows)
    },
    downloadCoffeeShoppingList () {
      // Welcome bags only go to onsite + sharing-room dancers. Offsite
      // dancers fend for themselves at their hotel, so skip them.
      const entries = this.buildPeopleEntries()
        .filter(e => e.isDancer)
        .filter(e => e.room !== 'Staying offsite')
        .filter(e => e.coffee && e.coffee.trim())

      // Keyword groups for the totals tally. Patterns are scanned against
      // the lowercased preference text; each keyword counts at most once
      // per person. Mentions overlap on purpose — "almond milk" is one
      // bottle of almond milk, not a separate "milk" item.
      const KEYWORDS = [
        { label: 'Creamer / half-and-half', patterns: [/\bcreamers?\b/, /\bcream\b/, /\bhalf[- ]and[- ]half\b/, /\bhalf-and-half\b/] },
        { label: 'Sugar', patterns: [/\bsugars?\b/] },
        { label: 'Sweetener (Splenda / Stevia / etc.)', patterns: [/\bsplenda\b/, /\bstevia\b/, /\bsweet[- ]?n[- ]?low\b/, /\bsweeteners?\b/, /\bequal\b/] },
        { label: 'Honey', patterns: [/\bhoney\b/] },
        { label: 'Decaf coffee', patterns: [/\bdecaf/, /\bdecaffeinated\b/] },
        { label: 'Coffee (any)', patterns: [/\bcoffee\b/, /\bespresso\b/] },
        { label: 'Tea', patterns: [/\btea\b/] },
        { label: 'Almond milk', patterns: [/\balmond\b/] },
        { label: 'Oat milk', patterns: [/\boat\s*milk\b/, /\boatmilk\b/] },
        { label: 'Soy milk', patterns: [/\bsoy\b/] },
        { label: 'Coconut milk', patterns: [/\bcoconut\s*milk\b/] },
        { label: 'Milk (whole / 2% / skim)', patterns: [/\b(whole|2%|skim|regular)\s*milk\b/] },
        { label: 'Vanilla flavoring', patterns: [/\bvanilla\b/] },
        { label: 'Cinnamon', patterns: [/\bcinnamon\b/] }
      ]
      const counts = KEYWORDS.map(k => ({ label: k.label, count: 0 }))
      for (const e of entries) {
        const text = String(e.coffee || '').toLowerCase()
        KEYWORDS.forEach((k, i) => {
          if (k.patterns.some(p => p.test(text))) counts[i].count += 1
        })
      }
      const tally = counts.filter(c => c.count > 0)

      const lines = [
        `IABF ${this.adminFestivalYear} — Coffee & Tea Preferences`,
        '',
        'Filtered to onsite + sharing-room dancers (offsite skipped).',
        `${entries.length} ${entries.length === 1 ? 'person has' : 'people have'} entered a preference.`,
        '',
        '═══ BY PERSON (for welcome bags) ═══',
        '',
        ...entries.map(e => `- ${e.name}: ${e.coffee.trim()}`),
        '',
        '═══ TOTALS (for shopping) ═══',
        '',
        ...tally.map(t => `- ${t.label}: ${t.count}`),
        '',
        'Mentions are counted once per person; items can overlap',
        '(e.g. "decaf with cream" counts for both Decaf and Creamer).',
        'For anything not categorized above, scan the per-person list.'
      ]
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `iabf-${this.adminFestivalYear}-coffee-tea.txt`
      a.click()
      URL.revokeObjectURL(url)
    },
    facultyMealEntry (f, facultyFullName) {
      if (f.facultySource === 'record') {
        const meals = facultyMealsFromStay(f, this.adminFestivalYear)
        const all = meals.length === facultyMealsFromStay({}, this.adminFestivalYear).length
        return {
          ...this.guestEntry({
            ...f,
            guestName: facultyFullName,
            allMeals: all,
            meals: all ? [] : meals
          }, 'Faculty', ''),
          meals,
          allMeals: all
        }
      }
      return this.guestEntry(f, 'Faculty', '')
    },
    buildGuestRow (g, parentFullName, rowType) {
      const isFree = g.type === 'faculty_guest'
      let mealsLabel
      if (g.allMeals) {
        mealsLabel = isFree ? 'All Meals' : 'All Meals ($500)'
      } else if (g.meals && g.meals.length) {
        mealsLabel = g.meals.map(k => MEAL_ABBREV[k] || k).join(', ')
      } else {
        mealsLabel = 'None'
      }
      return {
        id: g.id,
        rowType: rowType || 'guest',
        name: g.guestName || '',
        dancerNameDisplay: parentFullName,
        mealsLabel,
        arriveDisplay: '',
        leaveDisplay: '',
        dietaryDisplay: dietaryDisplayLine(g) || g.dietaryRestriction || '',
        allergiesDisplay: allergiesDisplayLine(g) || g.otherAllergies || '',
        notesDisplay: [g.dietaryNotes, g.notes].filter(Boolean).join(' · ') || '',
        statusDisplay: isFree ? '' : g.paymentStatus,
        paymentStatus: isFree ? null : g.paymentStatus,
        dancerId: g.dancerId
      }
    },
    buildFacultyRow (f) {
      const name = f.guestName || [f.firstName, f.lastName].filter(Boolean).join(' ')
      const stay = f.facultySource === 'record'
        ? facultyStayDates(f, this.adminFestivalYear)
        : null
      let mealsLabel
      if (f.facultySource === 'record') {
        mealsLabel = facultyMealsLabel(f, this.adminFestivalYear)
      } else if (f.allMeals) {
        mealsLabel = 'All Meals'
      } else if (f.meals && f.meals.length) {
        mealsLabel = f.meals.map(k => MEAL_ABBREV[k] || k).join(', ')
      } else {
        mealsLabel = 'None'
      }
      return {
        id: f.id,
        rowType: 'faculty',
        facultySource: f.facultySource || 'legacy',
        name,
        dancerNameDisplay: '',
        arriveDisplay: stay ? formatFacultyStayDate(stay.arrival) : '',
        leaveDisplay: stay ? formatFacultyStayDate(stay.departure) : '',
        mealsLabel,
        dietaryDisplay: dietaryDisplayLine(f) || f.dietaryRestriction || '',
        allergiesDisplay: allergiesDisplayLine(f) || f.otherAllergies || '',
        notesDisplay: [f.dietaryNotes, f.notes].filter(Boolean).join(' · ') || '',
        statusDisplay: '',
        paymentStatus: null
      }
    },
    rowStyleClass (row) {
      if (row.rowType === 'dancer') return 'dancer-row'
      if (row.rowType === 'faculty') return 'faculty-row'
      if (row.rowType === 'faculty_guest') return 'faculty-guest-row'
      return 'guest-row'
    },
    statusColor (status) {
      if (status === 'paid') return 'green'
      if (status === 'added_to_next') return 'blue darken-1'
      return 'orange darken-1'
    },
    statusLabel (status) {
      if (status === 'paid') return 'Paid'
      if (status === 'added_to_next') return 'Added to next payment'
      return 'Unpaid'
    },
    markPaid (row) {
      this.$set(this.markingPaidIds, row.id, true)
      db.collection(this.IABF.guestRegistrations).doc(row.id).update({ paymentStatus: 'paid', paidAt: new Date() })
        .then(() => {
          const idx = this.guestRegs.findIndex(g => g.id === row.id)
          if (idx !== -1) this.$set(this.guestRegs[idx], 'paymentStatus', 'paid')
          this.$delete(this.markingPaidIds, row.id)
        })
        .catch(e => {
          console.error('markPaid error:', e)
          this.$delete(this.markingPaidIds, row.id)
        })
    },
    openAddDialog (row) {
      this.form = emptyForm()
      this.saveError = ''
      if (row) {
        if (row.rowType === 'faculty') {
          this.form.type = 'faculty_guest'
          this.form.dancerId = row.id
          this.preselectedDancerId = row.id
        } else if (row.rowType === 'dancer') {
          this.form.type = 'guest'
          this.form.dancerId = row.id
          this.preselectedDancerId = row.id
        } else {
          this.form.type = 'guest'
          this.form.dancerId = row.dancerId || null
          this.preselectedDancerId = this.form.dancerId
        }
      } else {
        this.preselectedDancerId = null
      }
      this.showAddDialog = true
    },
    openEditDialog (row) {
      this.saveError = ''
      this.editingGuestId = row.id
      this.preselectedDancerId = row.dancerId || null
      // Find the original guest record for full data
      const g = this.guestRegs.find(r => r.id === row.id) || row
      this.form = {
        type: g.type || 'guest',
        dancerId: g.dancerId || null,
        guestName: g.guestName || '',
        attendingShow: g.attendingShow ?? null,
        awardsAfterparty: g.awardsAfterparty ?? null,
        afterpartyOption: g.afterpartyOption || '',
        lodging: g.lodging || '',
        allMeals: g.allMeals || false,
        meals: g.meals ? [...g.meals] : [],
        diet: g.diet || g.dietaryRestriction || '',
        gluten: g.gluten || '',
        foodAllergies: Array.isArray(g.foodAllergies)
          ? [...g.foodAllergies]
          : (g.otherAllergies ? [g.otherAllergies] : []),
        dietaryNotes: g.dietaryNotes || '',
        notes: g.notes || '',
        paymentStatus: g.paymentStatus || 'unpaid',
        priceOverride: g.totalCost != null ? g.totalCost : null
      }
      this.showAddDialog = true
    },
    closeAddDialog () {
      this.showAddDialog = false
      this.preselectedDancerId = null
      this.editingGuestId = null
      this.form = emptyForm()
      this.saveError = ''
    },
    openFacultyStayDialog (row) {
      const f = this.facultyRecords.find(r => r.id === row.id)
      const stay = facultyStayDates(f || {}, this.adminFestivalYear)
      this.facultyStayError = ''
      this.facultyStayForm = {
        id: row.id,
        name: row.name,
        arrivalDate: stay.arrival,
        departureDate: stay.departure
      }
      this.showFacultyStayDialog = true
    },
    closeFacultyStayDialog () {
      this.showFacultyStayDialog = false
      this.facultyStayForm = null
      this.facultyStayError = ''
    },
    async saveFacultyStay () {
      if (!this.facultyStayForm) {
        return
      }
      this.savingFacultyStay = true
      this.facultyStayError = ''
      try {
        const arrivalDate = String(this.facultyStayForm.arrivalDate || '').trim()
        const departureDate = String(this.facultyStayForm.departureDate || '').trim()
        await this.$store.dispatch('updateIabfFaculty', {
          id: this.facultyStayForm.id,
          arrivalDate,
          departureDate
        })
        const idx = this.facultyRecords.findIndex(r => r.id === this.facultyStayForm.id)
        if (idx !== -1) {
          this.$set(this.facultyRecords[idx], 'arrivalDate', arrivalDate)
          this.$set(this.facultyRecords[idx], 'departureDate', departureDate)
        }
        this.closeFacultyStayDialog()
      } catch (e) {
        console.error('save faculty stay', e)
        this.facultyStayError = 'Could not save dates. Please try again.'
      }
      this.savingFacultyStay = false
    },
    async deleteCurrent () {
      if (!this.editingGuestId) return
      const label =
        this.form.type === 'faculty' ? 'faculty member'
          : this.form.type === 'faculty_guest' ? 'faculty guest'
            : 'guest'
      const name = this.form.guestName || 'this entry'
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete ${label} "${name}"? This cannot be undone.`)) return
      this.deletingCurrent = true
      try {
        const id = this.editingGuestId
        await db.collection(this.IABF.guestRegistrations).doc(id).delete()
        const idx = this.guestRegs.findIndex(r => r.id === id)
        if (idx !== -1) this.guestRegs.splice(idx, 1)
        this.closeAddDialog()
      } catch (e) {
        console.error('deleteCurrent error:', e)
        this.saveError = 'Could not delete. Please try again.'
      }
      this.deletingCurrent = false
    },
    async deleteFaculty (row) {
      const label = row.rowType === 'faculty' ? 'faculty member' : 'faculty guest'
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Delete this ${label} (${row.name})?`)) return
      this.$set(this.deletingIds, row.id, true)
      try {
        await db.collection(this.IABF.guestRegistrations).doc(row.id).delete()
        const idx = this.guestRegs.findIndex(r => r.id === row.id)
        if (idx !== -1) this.guestRegs.splice(idx, 1)
      } catch (e) {
        console.error('deleteFaculty error:', e)
      }
      this.$delete(this.deletingIds, row.id)
    },
    onAllMealsChange () {
      if (this.form.allMeals) {
        this.form.meals = []
      } else if (this.form.awardsAfterparty && AFTERPARTY_TO_MEAL[this.form.afterpartyOption]) {
        this.form.meals = [AFTERPARTY_TO_MEAL[this.form.afterpartyOption]]
      }
    },
    syncBanquetFromAfterparty () {
      if (this.form.allMeals) return
      const mealKey = this.form.awardsAfterparty ? AFTERPARTY_TO_MEAL[this.form.afterpartyOption] : null
      this.form.meals = this.form.meals.filter(k => !['fri_banquet', 'fri_banquet_transport'].includes(k))
      if (mealKey) this.form.meals.push(mealKey)
    },
    onBanquetCheck (key) {
      if (!['fri_banquet', 'fri_banquet_transport'].includes(key)) return
      if (this.form.meals.includes(key)) {
        this.form.meals = this.form.meals.filter(k => k === key || !['fri_banquet', 'fri_banquet_transport'].includes(k))
      }
    },
    async saveGuest () {
      this.saving = true
      this.saveError = ''
      const isFree = this.form.type === 'faculty' || this.form.type === 'faculty_guest'
      let parentName = ''
      let parentEmail = ''
      if (this.form.type === 'faculty_guest') {
        const faculty = this.facultyPeopleForTable().find(f => f.id === this.form.dancerId)
        parentName = faculty
          ? (faculty.guestName || [faculty.firstName, faculty.lastName].filter(Boolean).join(' '))
          : ''
      } else if (this.form.type === 'guest') {
        const dancer = this.allDancers.find(d => d.id === this.form.dancerId)
        parentName = dancer ? [dancer.firstName, dancer.lastName].filter(Boolean).join(' ') : ''
        parentEmail = dancer ? (dancer.email || '').toLowerCase() : ''
      }
      const parsed = this.form.type === 'faculty' ? parseName(this.form.guestName) : { firstName: '', lastName: '' }
      const payload = {
        type: this.form.type,
        dancerId: this.form.type === 'faculty' ? null : this.form.dancerId,
        dancerName: parentName,
        dancerEmail: parentEmail,
        guestName: this.form.guestName.trim(),
        attendingShow: isFree ? true : (this.form.attendingShow || false),
        awardsAfterparty: isFree ? true : (this.form.awardsAfterparty || false),
        afterpartyOption: isFree ? 'dinner_only' : (this.form.awardsAfterparty ? this.form.afterpartyOption : null),
        lodging: isFree ? null : (this.form.lodging || null),
        allMeals: this.form.allMeals,
        meals: this.form.allMeals ? [] : this.form.meals,
        diet: this.form.diet || '',
        dietDetails: this.form.dietDetails || [],
        gluten: this.form.gluten || '',
        foodsNotConsumed: buildFoodsNotConsumed(
          this.form.diet || '',
          this.form.dietDetails || [],
          this.form.gluten || ''
        ),
        foodAllergies: Array.isArray(this.form.foodAllergies) ? this.form.foodAllergies : [],
        dietaryNotes: this.form.dietaryNotes || '',
        // Legacy aliases kept in sync for older exports
        dietaryRestriction: this.form.diet || '',
        otherAllergies: (Array.isArray(this.form.foodAllergies)
          ? this.form.foodAllergies.filter(a => a !== 'None')
          : []).join(', '),
        notes: this.form.notes,
        totalCost: isFree ? 0 : this.dialogTotal,
        paymentStatus: isFree ? 'paid' : this.form.paymentStatus
      }
      if (this.form.type === 'faculty') {
        payload.firstName = parsed.firstName
        payload.lastName = parsed.lastName
      }
      try {
        if (this.editingGuestId) {
          await db.collection(this.IABF.guestRegistrations).doc(this.editingGuestId).update(payload)
          const idx = this.guestRegs.findIndex(r => r.id === this.editingGuestId)
          if (idx !== -1) this.$set(this.guestRegs, idx, { id: this.editingGuestId, ...this.guestRegs[idx], ...payload })
        } else {
          const ref = await db.collection(this.IABF.guestRegistrations).add({ ...payload, createdAt: new Date(), createdByAdmin: true })
          this.guestRegs.push({ id: ref.id, ...payload, createdAt: new Date(), createdByAdmin: true })
        }
        this.closeAddDialog()
      } catch (e) {
        console.error('saveGuest error:', e)
        this.saveError = 'Something went wrong. Please try again.'
      }
      this.saving = false
    }
  }
}
</script>

<style scoped>
h2 {
  font-family: Quattrocento, serif;
  font-weight: 400;
  font-size: 28px;
  letter-spacing: -.02em;
  margin: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.summary-bar {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.summary-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f5f3f0;
  border-radius: 6px;
  padding: 10px 18px;
  min-width: 90px;
}

.stat-num {
  font-family: Quattrocento, serif;
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
  text-align: center;
}

.highlight-stat {
  background: #efe9df;
  box-shadow: inset 0 0 0 2px #c9b89a;
}

.meal-headcount {
  background: #faf9f7;
  border: 1px solid #ece8e2;
  border-radius: 8px;
  padding: 12px 16px;
}

.meal-headcount-title {
  font-family: Quattrocento, serif;
  font-size: 15px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 10px;
}

.meal-headcount-note {
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  color: #999;
}

.meal-headcount-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
}

.meal-count-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 8px 6px;
}

.meal-count-num {
  font-family: Quattrocento, serif;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.1;
}

.meal-count-label {
  font-size: 11px;
  color: #888;
  margin-top: 2px;
  text-align: center;
}

.type-tag {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

.dancer-tag {
  background: #e8eaf6;
  color: #3949ab;
}

.faculty-tag {
  background: #fce4ec;
  color: #ad1457;
}

.faculty-guest-tag {
  background: #fff3e0;
  color: #e65100;
}

.form-section-label {
  font-weight: 600;
  font-size: 13px;
  color: #555;
  margin: 12px 0 4px 0;
  text-transform: uppercase;
  letter-spacing: .04em;
}

.meal-check-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}

.dialog-total {
  font-weight: 700;
  font-size: 18px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eee;
}
</style>

<style>
/* Unscoped — style VueGoodTable rows */
.dancer-row td {
  background: #f7f6ff !important;
  font-weight: 600;
}
.guest-row td {
  background: #ffffff;
  padding-left: 24px !important;
}
.faculty-row td {
  background: #fdf2f6 !important;
  font-weight: 600;
}
.faculty-guest-row td {
  background: #ffffff;
  padding-left: 24px !important;
}
</style>
